import {
  ArrowLeftRight,
  Bot,
  Boxes,
  Briefcase,
  Building,
  Building2,
  Cable,
  ClipboardList,
  Coins,
  Cpu,
  Database,
  Download,
  FileText,
  Gauge,
  House,
  Inbox,
  Info,
  KeyRound,
  Newspaper,
  Palette,
  Plug,
  PlayCircle,
  Puzzle,
  Rss,
  Settings,
  ShieldCheck,
  Sparkles,
  Target,
  Upload,
  UserRound,
  Users,
  Workflow,
} from "lucide-mui";
import type { AuthUser } from "../../api/client";
import { ORG_ADMIN_SCOPE, ORG_CONTACT_DATA_SCOPE } from "../../auth/orgRoutes";
import { userHasGlobalScope, userHasOrgScope } from "../../auth/scopes";

export type NavItem = {
  label: string;
  path: string;
  icon: React.ReactNode;
  /** Global scope required to see the item. */
  scope?: string;
  /** Scope required in the selected organization (global admins bypass it). */
  orgScope?: string;
  /** Muted line under the label (e.g. an app's tenant). */
  secondary?: string;
  /** Opens something (a dialog) instead of navigating; `path` is then only its key. */
  action?: NavAction;
  // A section: its pages, shown indented while the section is open.
  children?: { label: string; path: string }[];
};

export type NavAction = "run-agent";

/** A titled group of links in a sidebar flyout. */
export type NavSection = { heading?: string; items: NavItem[] };

export const tenantNavItems: NavItem[] = [
  // The organization's dashboard (the tenant root).
  { label: "Home", path: "", icon: <House /> },
  {
    // Every company anything prospected for the tenant, in one pipeline.
    label: "Prospects",
    path: "/prospects",
    icon: <Target />,
    children: [
      { label: "Overview", path: "/prospects" },
      // What needs a decision, what is being worked, what was finished.
      { label: "Inbox", path: "/prospects/inbox" },
      { label: "Worklist", path: "/prospects/worklist" },
      { label: "Done", path: "/prospects/done" },
      // Saved sets of prospects (prospect lists), made by hand, by an order or by a workflow.
      { label: "Collections", path: "/prospects/collections" },
    ],
  },
  // Place a prospect order and run the agent on it now (the order workflow's manual run).
  { label: "Run agent", path: "#run-agent", icon: <Sparkles />, action: "run-agent", orgScope: "org:write" },
  { label: "Workflows", path: "/workflows", icon: <Workflow />, scope: "signal_rules:read" },
  { label: "Settings", path: "/settings", icon: <Settings /> },
];

// Tenant-scoped data pages shown in Explore; linked under the active tenant.
export const exploreTenantNavItems: NavItem[] = [
  { label: "Companies", path: "/companies", icon: <Building2 /> },
  { label: "Contacts", path: "/contacts", icon: <Users /> },
];

export const exploreNavItems: NavItem[] = [
  { label: "Prospect orders", path: "/organization/orders", icon: <Inbox /> },
  { label: "Job ads", path: "/job-ads", icon: <Briefcase /> },
  { label: "Regulatory filings", path: "/regulatory-filings", icon: <FileText /> },
  { label: "Tenders", path: "/tenders", icon: <ClipboardList /> },
  { label: "News articles", path: "/news-articles", icon: <Newspaper /> },
  { label: "ATS feeds", path: "/ats-feeds", icon: <Rss /> },
];

export const platformNavItems: NavItem[] = [
  { label: "Platform health", path: "/platform", icon: <Gauge />, scope: "platform:read" },
  { label: "Organizations", path: "/settings/organizations", icon: <Building />, scope: "users:manage" },
  { label: "Users", path: "/settings/users", icon: <Users />, scope: "users:manage" },
  { label: "Imports", path: "/settings/imports", icon: <Upload />, scope: "imports:read" },
  { label: "Agent skills", path: "/settings/skills", icon: <Puzzle />, scope: "platform:read" },
];

// Developer-only tools, each behind a developer-only scope.
export const devNavItems: NavItem[] = [
  { label: "Database", path: "/settings/database", icon: <Database />, scope: "dev:read" },
  { label: "AI logs", path: "/settings/ai-generation-logs", icon: <Bot />, scope: "dev:read" },
  { label: "Roles & permissions", path: "/settings/roles-permissions", icon: <ShieldCheck />, scope: "dev:read" },
  { label: "UI kit", path: "/settings/ui-kit", icon: <Palette />, scope: "dev:read" },
  { label: "System prompts", path: "/settings/system-prompts", icon: <Bot />, scope: "system-prompts:read" },
  { label: "Agents", path: "/settings/agents", icon: <Bot />, scope: "agent-definitions:write" },
  { label: "Background jobs", path: "/settings/background-jobs", icon: <PlayCircle />, scope: "background-jobs:read" },
  { label: "Training data exports", path: "/settings/training-data-exports", icon: <Download />, scope: "training-data:read" },
  { label: "Inference instances", path: "/settings/inference-instances", icon: <Cpu />, scope: "inference-instances:manage" },
  { label: "Data providers", path: "/settings/data-providers", icon: <Coins />, scope: "data-providers:manage" },
  { label: "News feeds", path: "/settings/news-feeds", icon: <Rss />, scope: "news-feeds:manage" },
  { label: "Unmerge review", path: "/merge-review", icon: <ArrowLeftRight />, scope: "merge-suggestions:manage" },
];

// The selected organization's administration. Each entry mirrors its route
// guard in App.tsx, so a link never leads to a redirect.
export const orgAdminNavItems: NavItem[] = [
  { label: "Details", path: "/organization/details", icon: <Info />, orgScope: ORG_ADMIN_SCOPE },
  { label: "Members", path: "/organization/members", icon: <Users />, orgScope: ORG_ADMIN_SCOPE },
  { label: "Tenants", path: "/organization/tenants", icon: <Boxes />, orgScope: ORG_ADMIN_SCOPE },
  { label: "Contact data", path: "/organization/contact-data", icon: <Coins />, orgScope: ORG_CONTACT_DATA_SCOPE },
  { label: "Integrations", path: "/organization/integrations", icon: <Cable />, orgScope: ORG_ADMIN_SCOPE },
];

// The signed-in user's own pages (the Account menu).
export const accountNavItems: NavItem[] = [
  { label: "Profile", path: "/account/profile", icon: <UserRound /> },
  { label: "Security", path: "/account/security", icon: <KeyRound /> },
  { label: "Connected apps", path: "/account/apps", icon: <Plug /> },
];

/** Match on a path boundary so e.g. /signals-x would not also light up /signals.
 *  `to` may carry a query string (e.g. an app's ?tenant_id=); only its path counts. */
export function pathMatches(pathname: string, to: string): boolean {
  const toPath = to.split("?")[0];
  return pathname === toPath || pathname.startsWith(toPath + "/");
}

export function visibleItems(
  items: NavItem[],
  user: AuthUser | null,
  organizationId?: string | null,
): NavItem[] {
  return items.filter(
    (item) =>
      (!item.scope || userHasGlobalScope(user, item.scope)) &&
      (!item.orgScope || userHasOrgScope(user, organizationId, item.orgScope)),
  );
}
